import { useState, useCallback, useEffect } from 'react'
import Map, { Marker, Popup, NavigationControl, GeolocateControl, FullscreenControl, ScaleControl } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MapPin, Filter } from 'lucide-react'
import { getMapData, MapLocation } from '../api/map'
import { hasValidCoordinates } from '../utils/geo'

interface MapComponentProps {
  height?: string
  showControls?: boolean
  showFilters?: boolean
  /** Center map on this point and show a "You" marker */
  userLocation?: { latitude: number; longitude: number } | null
  /** Initial filter state */
  initialFilters?: { foodBanks?: boolean; suppliers?: boolean }
}

const MapComponent = ({
  height = 'h-96',
  showControls = true,
  showFilters = true,
  userLocation = null,
  initialFilters
}: MapComponentProps) => {
  const [viewState, setViewState] = useState({
    // Default: Durham–Chapel Hill corridor (not Raleigh)
    longitude: userLocation?.longitude ?? -78.98,
    latitude: userLocation?.latitude ?? 35.94,
    zoom: userLocation ? 11 : 10
  })

  const [popupInfo, setPopupInfo] = useState<MapLocation | null>(null)
  const [locations, setLocations] = useState<MapLocation[]>([])
  const [filteredLocations, setFilteredLocations] = useState<MapLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    foodBanks: initialFilters?.foodBanks ?? true,
    suppliers: initialFilters?.suppliers ?? true
  })
  
  // Check if Mapbox token is available
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoiZ2FycmJlYXItMDkiLCJhIjoiY21keGZqYXd2MDJ4MzJsb2xiOWF4MGxjNyJ9.GefZT_BK-11i70G9CuI2TA'
  
  // Debug: Log token status
  console.log('🔍 Mapbox token status:', mapboxToken ? 'Present' : 'Missing')
  console.log('🔍 Token preview:', mapboxToken ? `${mapboxToken.substring(0, 20)}...` : 'None')
  console.log('🔍 Environment check:', {
    VITE_MAPBOX_TOKEN: import.meta.env.VITE_MAPBOX_TOKEN,
    NODE_ENV: import.meta.env.NODE_ENV,
    MODE: import.meta.env.MODE
  })
  
  // Define all hooks and functions before any conditional returns
  const onMarkerClick = useCallback((event: any, location: MapLocation) => {
    event.originalEvent.stopPropagation()
    setPopupInfo(location)
  }, [])

  const getPinClassName = useCallback((type: string) => {
    switch (type) {
      case 'food_bank':
        return 'h-9 w-9 text-primary-600 drop-shadow-md'
      case 'supplier':
        return 'h-9 w-9 text-secondary-600 drop-shadow-md'
      default:
        return 'h-9 w-9 text-gray-600 drop-shadow-md'
    }
  }, [])
  
  // Fetch map data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getMapData()
        const withCoords = data.filter((loc) =>
          hasValidCoordinates(loc.latitude, loc.longitude)
        )
        setLocations(withCoords)
        setFilteredLocations(withCoords)
      } catch (error) {
        console.error('Error fetching map data:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [])

  // Recenter when user location becomes available
  useEffect(() => {
    if (!userLocation) return
    setViewState((prev) => ({
      ...prev,
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      zoom: Math.max(prev.zoom, 11)
    }))
  }, [userLocation])

  // Filter locations based on selected filters
  useEffect(() => {
    const filtered = locations.filter(location => {
      if (location.type === 'food_bank' && !filters.foodBanks) return false
      if (location.type === 'supplier' && !filters.suppliers) return false
      return true
    })
    setFilteredLocations(filtered)
  }, [locations, filters])
  
  // Render placeholder if no Mapbox token
  if (!mapboxToken || mapboxToken === 'your_mapbox_access_token_here') {
    return (
      <div className={`w-full ${height} rounded-lg overflow-hidden shadow-lg bg-gray-100 flex items-center justify-center`}>
        <div className="text-center max-w-4xl mx-auto p-6">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Interactive Map</h3>
          <p className="text-gray-600 mb-6">Map will appear here once configured with Mapbox token</p>
          
          {/* Show data summary */}
          {!loading && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Available Locations</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                  <div className="w-8 h-8 bg-primary-100 border-2 border-primary-500 rounded-full mx-auto mb-2"></div>
                  <span className="text-sm text-gray-600">Food Banks</span>
                  <div className="text-2xl font-bold text-primary-600 mt-1">
                    {filteredLocations.filter(l => l.type === 'food_bank').length}
                  </div>
                </div>
                <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                  <div className="w-8 h-8 bg-secondary-100 border-2 border-secondary-500 rounded-full mx-auto mb-2"></div>
                  <span className="text-sm text-gray-600">Suppliers</span>
                  <div className="text-2xl font-bold text-secondary-600 mt-1">
                    {filteredLocations.filter(l => l.type === 'supplier').length}
                  </div>
                </div>
              </div>
              
              {/* Show list of locations */}
              {filteredLocations.length > 0 && (
                <div className="bg-white rounded-lg p-4 shadow-sm max-h-64 overflow-y-auto">
                  <h5 className="font-semibold text-gray-800 mb-3">Location List</h5>
                  <div className="space-y-2">
                    {filteredLocations.slice(0, 10).map((location) => (
                      <div key={location.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                        <div className={`w-4 h-4 rounded-full ${
                          location.type === 'food_bank' ? 'bg-primary-500' : 'bg-secondary-500'
                        }`}></div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{location.name}</div>
                          <div className="text-sm text-gray-600">{location.city}, {location.state}</div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {location.type === 'food_bank' ? 'Food Bank' : 'Supplier'}
                        </div>
                      </div>
                    ))}
                    {filteredLocations.length > 10 && (
                      <div className="text-center text-sm text-gray-500 pt-2">
                        ... and {filteredLocations.length - 10} more locations
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="text-sm text-gray-500">
            To enable the interactive map, add your Mapbox token to the environment variables
          </div>
        </div>
      </div>
    )
  }

  // Render loading state
  if (loading) {
    return (
      <div className={`w-full ${height} rounded-lg overflow-hidden shadow-lg bg-gray-100 flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`w-full ${height} rounded-lg overflow-hidden shadow-lg relative`}>
      {/* Filter Controls */}
      {showFilters && (
        <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.foodBanks}
                onChange={(e) => setFilters(prev => ({ ...prev, foodBanks: e.target.checked }))}
                className="rounded"
              />
              <MapPin className="h-4 w-4 text-primary-600" fill="currentColor" />
              <span>Food Banks</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.suppliers}
                onChange={(e) => setFilters(prev => ({ ...prev, suppliers: e.target.checked }))}
                className="rounded"
              />
              <MapPin className="h-4 w-4 text-secondary-600" fill="currentColor" />
              <span>Suppliers</span>
            </label>
            {userLocation && (
              <div className="flex items-center gap-2 text-sm pt-1 border-t border-gray-100">
                <MapPin className="h-4 w-4 text-orange-600" fill="currentColor" />
                <span>You</span>
              </div>
            )}
          </div>
        </div>
      )}

      <Map
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={mapboxToken}
        style={{ width: '100%', height: '100%' }}
        onError={(error) => {
          console.error('🗺️ Mapbox error:', error)
        }}
      >
        {showControls && (
          <>
            <NavigationControl position="top-right" />
            <GeolocateControl position="top-left" />
            <FullscreenControl position="top-right" />
            <ScaleControl position="bottom-left" />
          </>
        )}
        
        {filteredLocations.map((location) => (
          <Marker
            key={location.id}
            longitude={location.longitude}
            latitude={location.latitude}
            anchor="bottom"
            onClick={(e) => onMarkerClick(e, location)}
          >
            <button
              type="button"
              className="cursor-pointer hover:scale-110 transition-transform bg-transparent border-0 p-0"
              aria-label={location.name}
            >
              <MapPin
                className={getPinClassName(location.type)}
                fill="currentColor"
                stroke="white"
                strokeWidth={1.5}
              />
            </button>
          </Marker>
        ))}

        {userLocation && (
          <Marker
            longitude={userLocation.longitude}
            latitude={userLocation.latitude}
            anchor="bottom"
          >
            <MapPin
              className="h-10 w-10 text-orange-600 drop-shadow-md"
              fill="currentColor"
              stroke="white"
              strokeWidth={1.5}
            />
          </Marker>
        )}

        {popupInfo && (
          <Popup
            anchor="top"
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
            className="z-10"
          >
            <div className="p-4 max-w-xs">
              <h3 className="font-semibold text-gray-900 text-lg mb-2">{popupInfo.name}</h3>
              <p className="text-sm text-gray-600 mb-3">{popupInfo.description}</p>
              
              {popupInfo.street && (
                <div className="text-sm text-gray-600 mb-2">
                  📍 {popupInfo.street}, {popupInfo.city}, {popupInfo.state} {popupInfo.zipCode}
                </div>
              )}
              
              {popupInfo.phone && (
                <div className="text-sm text-gray-600 mb-2">
                  📞 {popupInfo.phone}
                </div>
              )}
              
              {popupInfo.type === 'food_bank' && popupInfo.capacity && (
                <div className="text-sm text-gray-600 mb-2">
                  📦 Capacity: {popupInfo.currentInventory}/{popupInfo.capacity}
                </div>
              )}
              
              {popupInfo.type === 'food_bank' && popupInfo.hours && (
                <div className="text-sm text-green-600 mb-2 font-medium">
                  📅 {popupInfo.hours}
                </div>
              )}
              
              {popupInfo.type === 'supplier' && popupInfo.businessType && (
                <div className="text-sm text-gray-600 mb-2">
                  🏢 {popupInfo.businessType.replace('_', ' ')}
                </div>
              )}
              
              <div className="mt-3">
                <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                  popupInfo.type === 'food_bank' ? 'bg-primary-100 text-primary-800' :
                  'bg-secondary-100 text-secondary-800'
                }`}>
                  {popupInfo.type === 'food_bank' ? 'Food Bank' : 'Supplier'}
                </span>
              </div>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  )
}

export default MapComponent 